import { useInView, motion } from 'framer-motion';
import { FC, ReactNode, useRef } from 'react';

interface Props {
  header: string;
  expand?: string;
  section: string;
  desc: string;
  src: ReactNode;
  tagColor: string;
  flexRowReverse?: boolean;
}
const hiddenX = { opacity: 0, x: 20 };
const visibleX = { opacity: 1, x: 0 };
const hiddenY = { opacity: 0, y: 20 };
const visibleY = { opacity: 1, y: 0 };
const transition = { duration: 0.5 };

const Compare: FC<Props> = ({
  header,
  expand,
  section,
  desc,
  src,
  tagColor,
  flexRowReverse = false,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { amount: 0.5, once: true });

  return (
    <div ref={ref} className="p-4 mb-4 lg:mx-2 bg-[#F8F8F8] w-full rounded">
      {/* for mobile & desktop */}
      <div className="relative w-full rounded md:hidden lg:block">
        {/* header */}
        <div className="relative flex flex-col lg:flex-row justify-center">
          <div className="relative w-fit mx-auto lg:mx-0 text-darkBlue self-center">
            <div className="relative text-p2Semibold px-3 w-fit mx-auto">
              <span
                className={`absolute top-0 left-0 w-[3px] h-full rounded`}
                style={{ background: tagColor }}
              />
              <motion.p
                initial={hiddenY}
                animate={isInView ? visibleY : hiddenY}
                transition={{ ...transition, delay: 0.3 }}
              >
                {header}
              </motion.p>
            </div>
            {expand && (
              <motion.p
                className="lg:absolute text-p4Regular pt-1 lg:pl-3 lg:w-[150%] text-center lg:text-left"
                initial={hiddenY}
                animate={isInView ? visibleY : hiddenY}
                transition={{ ...transition, delay: 0.5 }}
              >
                {expand}
              </motion.p>
            )}
          </div>

          <div className="relative text-p2Semibold whitespace-nowrap">
            <motion.p
              className="px-[13px] py-1 rounded-2xl bg-[#E2EEFF] w-fit mt-6 lg:my-0 mx-auto"
              initial={hiddenY}
              animate={isInView ? visibleY : hiddenY}
              transition={{ ...transition, delay: 0.35 }}
            >
              {section}
            </motion.p>
          </div>
        </div>

        {/* image */}
        <div className={`relative flex items-center my-5 h-[200px]`}>
          <motion.div
            className="w-fit mx-auto"
            initial={hiddenY}
            animate={isInView ? visibleY : hiddenY}
            transition={{ ...transition, delay: 0.5 }}
          >
            {src}
          </motion.div>
        </div>

        {/* description */}
        <motion.div
          className="relative w-full mt-4 text-p3Regular text-black"
          initial={hiddenY}
          animate={isInView ? visibleY : hiddenY}
          transition={{ ...transition, delay: 0.7 }}
        >
          {desc}
        </motion.div>
      </div>

      {/* for tablet */}
      <div className="hidden relative w-full md:block lg:hidden">
        <div
          className={`relative flex ${flexRowReverse && 'flex-row-reverse'}`}
        >
          {/* image */}
          <div className="relative flex h-[166px]">
            <motion.div
              className="w-fit"
              initial={{ opacity: 0, x: flexRowReverse ? 20 : -20 }}
              animate={
                isInView
                  ? visibleX
                  : { opacity: 0, x: flexRowReverse ? 20 : -20 }
              }
              transition={{ ...transition, delay: 0.3 }}
            >
              {src}
            </motion.div>
          </div>

          <div className="relative flex flex-col w-[80%] px-3">
            {/* header */}
            <div className="relative flex text-darkBlue mb-5">
              <div className="pt-1">
                <motion.div
                  className="relative text-p2Semibold px-3"
                  initial={hiddenX}
                  animate={isInView ? visibleX : hiddenX}
                  transition={{ ...transition, delay: 0.3 }}
                >
                  <span
                    className={`absolute top-0 left-0 w-[3px] h-full rounded`}
                    style={{ background: tagColor }}
                  />
                  {header}
                </motion.div>

                {expand && (
                  <motion.p
                    className="absolute text-p4Regular pt-2 pl-3 w-[150%]"
                    initial={hiddenX}
                    animate={isInView ? visibleX : hiddenX}
                    transition={{ ...transition, delay: 0.5 }}
                  >
                    {expand}
                  </motion.p>
                )}
              </div>

              <div className="relative text-p2Semibold whitespace-nowrap">
                <motion.p
                  className="px-[13px] py-1 rounded-2xl bg-[#E2EEFF] w-fit"
                  initial={hiddenY}
                  animate={isInView ? visibleY : hiddenY}
                  transition={{ ...transition, delay: 0.35 }}
                >
                  {section}
                </motion.p>
              </div>
            </div>

            {/* description */}
            <motion.div
              className="relative w-full mt-4 text-p3Regular text-black"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ ...transition, delay: 0.5 }}
            >
              {desc}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Compare;
